import React from "react";

import { Button, Image } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { withTrackEvent } from "@newsela/snitch";
import PossibleTokens from "@/challenges/PossibleTokens";
import getChallengesBreadcrumbsLinks from "@/challenges/utils/getChallengeBreadcrumsLinks";
import DynamicBreadcrumbs from "@/components/DynamicBreadcrumbs";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import ChallengeLearnMoreModal from "../../ChallengeLearnMoreModal/ChallengeLearnMoreModal";
import {
  breadcrumbCss,
  descriptionCss,
  headerContainerCss,
  headerContentInfoCss,
  headerContentTop,
  headingCss,
  headingImageCss,
  possibleTokensContentCss,
  linkWrapper,
} from "./ChallengesHeader.style";

const propTypes = {
  description: PropTypes.string,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  activeBreadcrumbs: PropTypes.bool,
  image: PropTypes.object,
  totalArticles: PropTypes.number,
  totalInteractions: PropTypes.number,
  totalSkills: PropTypes.number,
  countdown: PropTypes.number,
  tokens: PropTypes.number,
  month: PropTypes.number,
  title: PropTypes.string,
  date: PropTypes.string,
  classes: PropTypes.arrayOf(PropTypes.string),
  challengeId: PropTypes.string,
  isHomeVersion: PropTypes.bool,
  hasImage: PropTypes.bool,
};

const ChallengesHeader = ({
  activeBreadcrumbs = true,
  countdown,
  date,
  heading,
  month,
  hasImage,
  isHomeVersion,
  challengeId,
  tokens,
  image,
}) => {
  const { data: user } = useUser();
  const { isStudent, isTeacher } = getUserProperties(user);
  const ButtonWithTrackEvent = withTrackEvent(Button);
  const eventProperties = {
    objectType: "User",
    actionName: "Navigate",
    event_type: "click",
    streamProperties: {
      application: "parnassus",
      component_type: "ChallengeHeader",
      content_id: challengeId,
      content_type: "Challenge",
      description: "Redirect to Challenge Page",
    },
  };

  const router = useRouter();
  const showDateAndCountdown = month && date && countdown;
  const shouldRenderPossibleTokenBadge =
    (isStudent && !showDateAndCountdown) || hasImage;
  const breadcrumbLinks = getChallengesBreadcrumbsLinks(heading);

  return (
    <div css={headerContainerCss}>
      <div css={headerContentInfoCss}>
        {activeBreadcrumbs && breadcrumbLinks && (
          <DynamicBreadcrumbs
            breadcrumbsCss={breadcrumbCss}
            breadcrumbsLinks={breadcrumbLinks}
          />
        )}
        <div css={headerContentTop}>
          <div css={headerContentTop}>
            <h1 css={headingCss}>{heading}</h1>
            {!isTeacher && isHomeVersion && (
              <ButtonWithTrackEvent
                onClick={() => router.push("/challenges")}
                __cssFor={{ root: linkWrapper }}
                flavor={Button.flavors.muted}
                {...eventProperties}
                dataProps={{
                  "data-qa-selector": "IRC: Redirect to Challenge page",
                }}
              >
                View all topics
              </ButtonWithTrackEvent>
            )}
          </div>
        </div>
        {shouldRenderPossibleTokenBadge && isTeacher && (
          <div css={possibleTokensContentCss}>
            <PossibleTokens tokens={tokens} />
          </div>
        )}
        {isStudent && (
          <p css={descriptionCss}>
            Welcome to this month's reading challenge! Read articles that
            interests you most and earn tokens for every quiz, Power Words and
            Guided Highlighting activity you complete. Earn your monthly badge
            when you reach your reading goal!
          </p>
        )}
        {!isStudent && (
          <p css={descriptionCss}>
            Each month, students can read articles that interest them and earn
            tokens for every quiz, Power Words and Guided Highlighting activity
            completed. Students earn a badge when they reach their monthly
            reading goal.
            <ChallengeLearnMoreModal />
          </p>
        )}
      </div>
      {hasImage && (
        <Image alt={image?.alt} src={image?.url} __css={headingImageCss} />
      )}
    </div>
  );
};

ChallengesHeader.propTypes = propTypes;

export default ChallengesHeader;
